import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Training } from '../../services/firebase/training.service';
import { ShareTrainingModal } from '../ShareTraining';
import Button from './Button';
import Menu from './Menu';

interface ShareOption {
  id: string;
  label: string;
  icon: string;
  action: () => void;
}

interface ShareButtonProps {
  training?: Training;
  useModal?: boolean;
  className?: string;
  iconOnly?: boolean;
  
  // New props for enhanced functionality
  options?: ShareOption[];
  buttonLabel?: string;
  buttonVariant?: 'primary' | 'secondary' | 'outlined' | 'outline' | 'text' | 'ghost' | 'danger';
  title?: string;
}

/**
 * ShareButton component
 * 
 * @description A button for sharing content
 * 
 * Can be used in two ways:
 * 1. Training-specific sharing (legacy mode) - provide training prop
 * 2. General content sharing - provide options prop with custom share actions
 */
const ShareButton: React.FC<ShareButtonProps> = ({ 
  training, 
  useModal = false, 
  className = '', 
  iconOnly = false,
  options,
  buttonLabel,
  buttonVariant = 'primary',
  title
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const defaultButtonLabel = buttonLabel || t('common.actions.share') as string;
  const defaultTitle = title || t('common.actions.share') as string;

  // Handle legacy training share
  const handleTrainingShare = () => {
    if (useModal && training) {
      setIsModalOpen(true);
    } else if (training) {
      navigate(`/training/${training.id}/share`);
    }
  };
  
  // Handle generic share with options
  const handleGenericShare = () => {
    if (options && options.length > 0) {
      setIsMenuOpen(true);
    }
  };
  
  const handleShare = () => {
    if (training) {
      handleTrainingShare();
    } else if (options) {
      handleGenericShare();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  
  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        onClick={handleShare}
        className={className}
        leftIcon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
          </svg>
        }
      >
        {!iconOnly && defaultButtonLabel}
      </Button>

      {/* Legacy training share modal */}
      {useModal && training && (
        <ShareTrainingModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          training={training}
        />
      )}
      
      {/* Generic share options menu */}
      {options && (
        <Menu
          isOpen={isMenuOpen}
          onClose={handleCloseMenu}
          title={defaultTitle}
          items={options.map(option => ({
            id: option.id,
            label: option.label,
            icon: option.icon,
            onClick: () => {
              option.action();
              handleCloseMenu();
            }
          }))}
        />
      )}
    </>
  );
};

export default ShareButton;